<template>
  <v-card class="d-flex flex-column" style="height: 100vh">
    <v-card-title class="error white--text d-flex align-center">
      <v-icon dark left>mdi-tshirt-crew</v-icon>
      WM Trikot 2026 Verwaltung
      <v-spacer></v-spacer>
      <v-btn icon dark @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-4 flex-grow-1" style="overflow-y: auto">
      <div v-if="loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="error" size="64"></v-progress-circular>
        <p class="mt-4">Lade Vorbestellungen...</p>
      </div>
      <div v-else>
        <v-row class="mb-4" align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              label="Suchen nach Name oder E-Mail..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              dense
              hide-details
              clearable></v-text-field>
          </v-col>
          <v-col cols="12" md="5">
            <v-chip-group v-model="statusFilter" mandatory active-class="error--text">
              <v-chip filter value="all">Alle</v-chip>
              <v-chip filter value="reserved">Reserviert</v-chip>
              <v-chip filter value="collected">Abgeholt / Bezahlt</v-chip>
              <v-chip filter value="canceled">Storniert</v-chip>
            </v-chip-group>
          </v-col>

          <v-col cols="12" md="2" class="text-right">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" v-bind="attrs" v-on="on" :disabled="filteredReservations.length === 0">
                  <v-icon left>mdi-download</v-icon>
                  Export
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="downloadCSV">
                  <v-list-item-title>📝 Einzelbestellungen</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadSummaryCSV">
                  <v-list-item-title>📊 Studio Aufteilung</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>

        <v-expansion-panels class="mb-6">
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-bold">
              <div class="d-flex align-center">
                <v-icon left color="primary" class="mr-2">mdi-chart-box-outline</v-icon>
                Zusammenfassung (Total & Pro Studio) — {{ summary.total }} Trikots aktuell gefiltert
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="12" md="5">
                  <v-card outlined class="pa-4 h-100 grey lighten-4">
                    <div class="text-h6 mb-3 primary--text">Total Übersicht</div>
                    <div class="font-weight-bold mb-3 text-h5">{{ summary.total }} Stück</div>
                    <div v-for="(sizes, type) in summary.byTypeAndSize" :key="type" class="mb-3">
                      <strong class="text-subtitle-1">{{ type }}</strong>
                      <ul class="mb-0 pl-4">
                        <li v-for="(qty, size) in sizes" :key="size">
                          Grösse {{ size }}: <strong>{{ qty }}x</strong>
                        </li>
                      </ul>
                    </div>
                  </v-card>
                </v-col>

                <v-col cols="12" md="7">
                  <div class="d-flex align-center mb-4">
                    <div class="text-h6 primary--text mr-4">Pro Studio</div>
                    <v-select
                      v-model="selectedStudio"
                      :items="availableStudios"
                      label="Studio auswählen"
                      outlined
                      dense
                      hide-details
                      style="max-width: 300px"></v-select>
                  </div>

                  <v-card outlined class="pa-4" v-if="selectedStudio !== 'Alle' && summary.byStudio[selectedStudio]">
                    <div class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center">
                      <v-icon small left>mdi-domain</v-icon> {{ selectedStudio }}
                      <v-spacer></v-spacer>
                      <v-chip color="primary" outlined>Total: {{ summary.byStudio[selectedStudio].total }} Stück</v-chip>
                    </div>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                      <v-col cols="12" sm="6" v-for="(sizes, type) in summary.byStudio[selectedStudio].byTypeAndSize" :key="type">
                        <div class="mb-2 text-body-2">
                          <strong>{{ type }}</strong>
                          <ul class="mb-0 pl-4 text-caption">
                            <li v-for="(qty, size) in sizes" :key="size">
                              Grösse {{ size }}: <strong>{{ qty }}x</strong>
                            </li>
                          </ul>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                  <div v-else class="grey--text mt-4">
                    <v-icon left color="grey">mdi-hand-pointing-up</v-icon>
                    Bitte wähle oben ein Studio aus.
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-data-table :headers="headers" :items="filteredReservations" :items-per-page="15" class="elevation-1 rounded-lg">
          <template v-slot:item.createdBy="{ item }">
            <div v-if="item.createdBy">
              <div class="font-weight-bold">{{ item.createdBy.vorname }} {{ item.createdBy.nachname }}</div>
              <div class="text-caption grey--text">{{ item.createdBy.email }}</div>
            </div>
            <div v-else class="grey--text">Unbekannt</div>
          </template>

          <template v-slot:item.created="{ item }">
            {{ formatDate(item.created) }}
          </template>

          <template v-slot:item.content.items="{ item }">
            <div v-for="(trikot, index) in item.content.items" :key="index" class="text-caption">
              <strong>{{ trikot.quantity }}x</strong> {{ getVariantName(trikot.type) }} (Grösse: {{ trikot.size }})
            </div>
          </template>

          <template v-slot:item.content.totalPrice="{ item }">
            <div class="font-weight-bold">CHF {{ item.content.totalPrice ? Number(item.content.totalPrice).toFixed(2) : "0.00" }}</div>
          </template>

          <template v-slot:item.content.status="{ item }">
            <v-chip :color="getStatusColor(item.content.status)" small label text-color="white">
              {{ getStatusText(item.content.status) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <div v-if="item.content.status === 'reserved'" class="d-flex flex-column gap-2">
              <v-btn small color="success" class="mb-1" @click="updateStatus(item, 'collected')">
                <v-icon left small>mdi-cash-check</v-icon> Bezahlt
              </v-btn>
              <v-btn small color="error" outlined @click="showCancelDialog(item)"> Stornieren </v-btn>
            </div>
            <div v-else>
              <v-btn small color="grey" text disabled>{{ getStatusText(item.content.status) }}</v-btn>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>

    <v-dialog v-model="cancelDialog" max-width="400" persistent>
      <v-card v-if="itemToCancel">
        <v-card-title class="text-h5 warning--text">Vorbestellung stornieren?</v-card-title>
        <v-card-text>
          Möchtest du die Trikot-Bestellung von
          <strong>{{ itemToCancel.createdBy.vorname }} {{ itemToCancel.createdBy.nachname }}</strong> stornieren?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog = false">Abbrechen</v-btn>
          <v-btn color="warning" text @click="confirmCancel">Ja, stornieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  name: "TowelAdmin",
  data() {
    return {
      loading: true,
      reservations: [],
      search: "",
      statusFilter: "all",
      selectedStudio: "Alle",
      cancelDialog: false,
      itemToCancel: null,
      headers: [
        { text: "Kunde", value: "createdBy", sortable: true },
        { text: "Datum", value: "created", sortable: true },
        { text: "Bestellung", value: "content.items", sortable: false },
        { text: "Preis", value: "content.totalPrice", sortable: false },
        { text: "Studio", value: "createdBy.studio_id", sortable: true },
        { text: "Status", value: "content.status", sortable: true, align: "center" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],
      typeMap: {
        mann: "Männer",
        frau: "Frauen",
        junge: "Jungen",
        maedchen: "Mädchen",
      },
    }
  },
  computed: {
    filteredReservations() {
      let items = this.reservations

      if (this.selectedStudio !== "Alle") {
        items = items.filter((r) => (r.createdBy?.studio_id || "Unbekannt") === this.selectedStudio)
      }

      if (this.statusFilter !== "all") {
        items = items.filter((r) => r.content.status === this.statusFilter)
      }
      if (this.search && this.search.trim() !== "") {
        const searchTerm = this.search.toLowerCase().trim()
        items = items.filter((item) => {
          const createdBy = item.createdBy
          if (!createdBy) return false
          const fullName = `${createdBy.vorname || ""} ${createdBy.nachname || ""}`.toLowerCase()
          return fullName.includes(searchTerm) || (createdBy.email || "").toLowerCase().includes(searchTerm)
        })
      }
      return items
    },
    availableStudios() {
      const studios = new Set()
      this.reservations.forEach((res) => {
        studios.add(res.createdBy?.studio_id || "Unbekannt")
      })
      return ["Alle", ...Array.from(studios).sort()]
    },
    summary() {
      const result = { total: 0, byTypeAndSize: {}, byStudio: {} }
      this.filteredReservations.forEach((res) => {
        const studioId = res.createdBy?.studio_id || "Unbekannt"
        if (!result.byStudio[studioId]) result.byStudio[studioId] = { total: 0, byTypeAndSize: {} }

        res.content?.items?.forEach((item) => {
          const typeName = this.typeMap[item.type] || item.type
          const qty = Number(item.quantity) || 0
          result.total += qty

          if (!result.byTypeAndSize[typeName]) result.byTypeAndSize[typeName] = {}
          result.byTypeAndSize[typeName][item.size] = (result.byTypeAndSize[typeName][item.size] || 0) + qty

          result.byStudio[studioId].total += qty
          if (!result.byStudio[studioId].byTypeAndSize[typeName]) result.byStudio[studioId].byTypeAndSize[typeName] = {}
          result.byStudio[studioId].byTypeAndSize[typeName][item.size] = (result.byStudio[studioId].byTypeAndSize[typeName][item.size] || 0) + qty
        })
      })
      return result
    },
  },
  async created() {
    await this.fetchReservations()
  },
  methods: {
    getVariantName(type) {
      return this.typeMap[type] || type
    },
    async fetchReservations() {
      this.loading = true
      try {
        this.reservations = await api.getTrikotReservations()
      } catch (error) {
        this.$toast.error("Fehler beim Laden.")
      } finally {
        this.loading = false
      }
    },
    async updateStatus(item, newStatus) {
      try {
        const updatedItem = await api.updateTrikotReservationStatus(item._id, newStatus)
        const index = this.reservations.findIndex((r) => r._id === item._id)
        if (index !== -1) {
          const newReservations = [...this.reservations]
          newReservations[index] = updatedItem
          this.reservations = newReservations
        }
        this.$toast.success("Status aktualisiert.")
      } catch (error) {
        this.$toast.error("Fehler.")
      }
    },
    showCancelDialog(item) {
      this.itemToCancel = item
      this.cancelDialog = true
    },
    confirmCancel() {
      if (this.itemToCancel) this.updateStatus(this.itemToCancel, "canceled")
      this.cancelDialog = false
    },
    formatDate(ts) {
      return this.$moment.unix(ts).format("DD.MM.YY, HH:mm")
    },
    getStatusColor(s) {
      return s === "reserved" ? "warning" : s === "collected" ? "success" : "grey"
    },
    getStatusText(s) {
      const maps = { reserved: "Reserviert", collected: "Bezahlt", canceled: "Storniert" }
      return maps[s] || "Unbekannt"
    },

    // 1. Export: Liste aller einzelnen Vorbestellungen
    downloadCSV() {
      const headers = ["Nachname", "Vorname", "Email", "Studio", "Datum", "Status", "Bestellung", "Gesamtpreis (CHF)"]
      const rows = this.filteredReservations.map((res) => {
        const orderSummary = res.content.items.map((i) => `${i.quantity}x ${this.getVariantName(i.type)} (${i.size})`).join(" | ")
        return [
          res.createdBy?.nachname || "",
          res.createdBy?.vorname || "",
          res.createdBy?.email || "",
          res.createdBy?.studio_id || "Unbekannt",
          this.formatDate(res.created),
          this.getStatusText(res.content.status),
          `"${orderSummary}"`,
          res.content.totalPrice || "0",
        ]
      })
      this.triggerDownload(headers, rows, "Kundenliste")
    },

    // 2. Export: Zusammenfassung der Anzahl pro Grösse & Studio
    downloadSummaryCSV() {
      const headers = ["Studio", "Kategorie", "Grösse", "Anzahl"]
      const rows = []

      // Iteriere durch die berechnete Zusammenfassung (this.summary)
      for (const [studioId, studioData] of Object.entries(this.summary.byStudio)) {
        for (const [typeName, sizes] of Object.entries(studioData.byTypeAndSize)) {
          for (const [size, qty] of Object.entries(sizes)) {
            rows.push([studioId, typeName, size, qty])
          }
        }
      }

      // Sortiere das Ergebnis alphabetisch nach Studio und dann nach Kategorie
      rows.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]))

      this.triggerDownload(headers, rows, "Studio_Aufteilung")
    },

    // Hilfsfunktion für den eigentlichen Datei-Download
    triggerDownload(headers, rows, fileSuffix) {
      const csvContent = [headers, ...rows].map((e) => e.join(";")).join("\n")
      // \ufeff ist wichtig für korrekte Umlaut-Darstellung in Excel (UTF-8 BOM)
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" })
      const link = document.createElement("a")
      const url = URL.createObjectURL(blob)
      link.setAttribute("href", url)
      link.setAttribute("download", `Trikot_${fileSuffix}_${this.$moment().format("YYYY-MM-DD")}.csv`)
      link.style.visibility = "hidden"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
.h-100 {
  height: 100%;
}
</style>
